import React, { useEffect, useState } from 'react';

import {
  AnimatePresence,
  motion
} from 'motion/react';

import {
  AlertTriangle,
  Play,
  X,
  ShieldCheck,
  FolderOpen,
  Monitor,
  FileCode2,
  Terminal,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

import {
  ScriptCandidate
} from '@kaizen/shared';

import {
  usePrivacy
} from '../../contexts/PrivacyContext';

import {
  kaizenAutoHeightTransition,
  kaizenDialogTransition,
  kaizenOverlayTransition
} from '../../lib/kaizenMotion';


interface RunScriptModalProps {
  script:
    ScriptCandidate |
    null;

  workingDirectory:
    string;

  projectName?:
    string;

  isOpen:
    boolean;

  onClose:
    () => void;

  onConfirm:
    (
      script:
        ScriptCandidate
    ) => void;
}


export const RunScriptModal:
React.FC<RunScriptModalProps> = ({
  script,
  workingDirectory,
  projectName,
  isOpen,
  onClose,
  onConfirm
}) => {

  const {
    displayPath
  } =
    usePrivacy();


  const [
    payloadExpanded,
    setPayloadExpanded
  ] =
    useState(false);


  useEffect(
    () => {

      if (isOpen) {
        setPayloadExpanded(false);
      }

    },
    [
      isOpen,
      script
    ]
  );


  if (
    !script
  ) {
    return null;
  }


  const payload =
    script.command ||
    script.scriptContent ||
    '';


  const isScript =
    script.type ===
    'powershell_script';


  const targetDirectory =
    script.workingDirectory ||
    workingDirectory;


  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={kaizenOverlayTransition}
          className="
            fixed inset-0 z-50
            flex items-center justify-center
            px-3 pt-3
            pb-[calc(6.5rem+env(safe-area-inset-bottom))]
            sm:p-5
            bg-slate-950/60
            backdrop-blur-xl
          "
        >

      {/* Ambient indigo illumination */}
      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute
          left-1/2 top-[18%]
          h-72 w-72
          -translate-x-1/2
          rounded-full
          bg-indigo-500/10
          blur-3xl
        "
      />


      <motion.div
        initial={{
          opacity: 0,
          y: 8,
          scale: 0.985
        }}
        animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }}
        exit={{
          opacity: 0,
          y: 6,
          scale: 0.99
        }}
        transition={kaizenDialogTransition}
        className="
          relative
          w-full
          max-w-xl
          max-h-[calc(100dvh-7.5rem)]
          sm:max-h-[88dvh]
          min-h-0
          flex flex-col
          overflow-hidden
          rounded-[18px]
          border border-white/10
          bg-slate-900/82
          shadow-[0_28px_90px_rgba(2,6,23,0.72)]
          backdrop-blur-2xl
        "
      >

        {/* ==================================================
            HEADER
        ================================================== */}

        <div
          className="
            shrink-0
            flex items-center justify-between
            gap-3
            px-4 sm:px-5
            py-4
            border-b border-white/[0.07]
            bg-white/[0.025]
          "
        >

          <div className="flex items-center gap-3 min-w-0">

            <div
              className="
                w-9 h-9
                shrink-0
                flex items-center justify-center
                rounded-xl
                border border-indigo-400/20
                bg-indigo-400/10
                text-indigo-300
                shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]
              "
            >
              <ShieldCheck className="w-[18px] h-[18px]" />
            </div>


            <div className="min-w-0">

              <h3
                className="
                  text-[15px] sm:text-base
                  font-semibold
                  text-slate-100
                  tracking-tight
                "
              >
                PowerShell Execution Approval
              </h3>

              <p className="text-[10px] sm:text-[11px] text-slate-500 mt-0.5">
                Review the payload before local execution
              </p>

            </div>

          </div>


          <button
            type="button"
            onClick={
              onClose
            }
            title="Close"
            className="
              w-9 h-9
              shrink-0
              flex items-center justify-center
              rounded-lg
              text-slate-500
              hover:text-slate-200
              hover:bg-white/[0.05]
              transition-colors
            "
          >
            <X className="w-4 h-4" />
          </button>

        </div>


        {/* ==================================================
            SCROLLABLE BODY
        ================================================== */}

        <div
          className="
            flex-1 min-h-0
            overflow-y-auto
            overscroll-contain
            px-4 sm:px-5
            py-4
            space-y-4
          "
        >

          {/* Approval Warning */}

          <div
            className="
              flex items-start
              gap-3
              px-3.5
              py-3
              rounded-xl
              border border-amber-400/15
              bg-amber-400/[0.07]
              shadow-[inset_0_1px_0_rgba(255,255,255,0.025)]
            "
          >

            <div
              className="
                w-8 h-8
                shrink-0
                flex items-center justify-center
                rounded-lg
                bg-amber-400/10
                text-amber-300
              "
            >
              <AlertTriangle className="w-4 h-4" />
            </div>


            <div className="min-w-0">

              <div className="text-[11px] sm:text-xs font-semibold text-amber-200">
                User approval required
              </div>

              <p className="text-[10px] sm:text-[11px] leading-relaxed text-amber-100/65 mt-0.5">
                This PowerShell payload will execute locally on your personal Windows host.
                Verify the target and contents before continuing.
              </p>

            </div>

          </div>


          {/* Metadata */}

          <div
            className="
              overflow-hidden
              rounded-xl
              border border-white/[0.07]
              bg-white/[0.025]
            "
          >

            {/* Action */}

            <div
              className="
                flex items-center
                gap-3
                px-3.5
                py-3
                border-b border-white/[0.06]
              "
            >

              <FileCode2 className="w-3.5 h-3.5 shrink-0 text-indigo-400" />

              <span className="text-[10px] text-slate-500 shrink-0">
                Action
              </span>

              <span
                className="
                  ml-auto
                  min-w-0
                  truncate
                  font-mono
                  text-[10px] sm:text-[11px]
                  font-medium
                  text-slate-200
                "
                title={
                  script.filename
                }
              >
                {script.filename}
              </span>

            </div>


            {/* Project */}

            <div
              className="
                flex items-center
                gap-3
                px-3.5
                py-3
                border-b border-white/[0.06]
              "
            >

              <Monitor className="w-3.5 h-3.5 shrink-0 text-indigo-400" />

              <span className="text-[10px] text-slate-500 shrink-0">
                Project
              </span>

              <span
                className="
                  ml-auto
                  min-w-0
                  truncate
                  text-[10px] sm:text-[11px]
                  font-medium
                  text-slate-200
                "
                title={
                  projectName ||
                  'General Execution'
                }
              >
                {projectName ||
                 'General Execution'}
              </span>

            </div>


            {/* Working Directory */}

            <div
              className="
                flex items-center
                gap-3
                px-3.5
                py-3
                border-b border-white/[0.06]
              "
            >

              <FolderOpen className="w-3.5 h-3.5 shrink-0 text-indigo-400" />

              <span className="text-[10px] text-slate-500 shrink-0">
                Directory
              </span>

              <span
                className="
                  ml-auto
                  min-w-0
                  truncate
                  font-mono
                  text-[9px] sm:text-[10px]
                  text-slate-300
                "
                title={
                  displayPath(
                    targetDirectory
                  )
                }
              >
                {displayPath(
                  targetDirectory
                )}
              </span>

            </div>


            {/* Execution Mode */}

            <div
              className="
                flex items-center
                gap-3
                px-3.5
                py-3
              "
            >

              <Terminal className="w-3.5 h-3.5 shrink-0 text-indigo-400" />

              <span className="text-[10px] text-slate-500 shrink-0">
                Mode
              </span>

              <div className="ml-auto flex items-center gap-2">

                <span
                  className="
                    inline-flex
                    px-2 py-1
                    rounded-md
                    border border-indigo-400/15
                    bg-indigo-400/[0.07]
                    font-mono
                    text-[9px] sm:text-[10px]
                    text-indigo-300
                  "
                >
                  {isScript
                    ? 'powershell_script'
                    : 'powershell_command'}
                </span>

              </div>

            </div>

          </div>


          {/* Payload */}

          <div className="space-y-2">

            <div className="flex items-center justify-between gap-3 px-0.5">

              <span
                className="
                  text-[10px]
                  uppercase
                  tracking-[0.14em]
                  font-semibold
                  text-slate-500
                "
              >
                PowerShell Payload
              </span>


              <div className="flex items-center gap-2.5">

                <span className="font-mono text-[9px] text-slate-600">
                  {payload.length.toLocaleString()} chars
                </span>


                <button
                  type="button"
                  onClick={
                    () =>
                      setPayloadExpanded(
                        current =>
                          !current
                      )
                  }
                  aria-expanded={
                    payloadExpanded
                  }
                  className="
                    inline-flex
                    items-center
                    gap-1.5
                    px-2.5
                    py-1.5
                    rounded-lg
                    border border-indigo-400/15
                    bg-indigo-400/[0.055]
                    text-[10px]
                    font-medium
                    text-indigo-300
                    shadow-[inset_0_1px_0_rgba(255,255,255,0.035)]
                    hover:border-indigo-400/25
                    hover:bg-indigo-400/[0.09]
                    hover:text-indigo-200
                    active:scale-[0.98]
                    transition-all
                  "
                >

                  <span>
                    {payloadExpanded
                      ? 'Hide'
                      : 'Review'}
                  </span>


                  {payloadExpanded
                    ? (
                      <ChevronUp className="w-3 h-3" />
                    )
                    : (
                      <ChevronDown className="w-3 h-3" />
                    )
                  }

                </button>

              </div>

            </div>


            <AnimatePresence initial={false}>

              {payloadExpanded && (

                <motion.div
                  initial={{
                    opacity: 0,
                    height: 0
                  }}
                  animate={{
                    opacity: 1,
                    height: 'auto'
                  }}
                  exit={{
                    opacity: 0,
                    height: 0
                  }}
                  transition={
                    kaizenAutoHeightTransition
                  }
                  className="
                    relative
                    overflow-hidden
                    rounded-xl
                    border border-slate-700/80
                    bg-[#020817]
                    shadow-[inset_0_1px_0_rgba(255,255,255,0.025)]
                  "
                >

                  <div
                    className="
                      flex items-center
                      gap-1.5
                      px-3
                      py-2
                      border-b border-slate-800
                      bg-slate-900/75
                    "
                  >

                    <span className="w-1.5 h-1.5 rounded-full bg-rose-400/60" />
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400/60" />
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400/60" />

                    <span className="ml-2 font-mono text-[9px] text-slate-600">
                      {isScript
                        ? 'script.ps1'
                        : 'command'}
                    </span>

                  </div>


                  <pre
                    className="
                      max-h-[34dvh]
                      sm:max-h-[360px]
                      overflow-auto
                      overscroll-contain
                      p-3.5
                      font-mono
                      text-[11px] sm:text-xs
                      leading-relaxed
                      text-slate-200
                      whitespace-pre-wrap
                      break-words
                      selection:bg-indigo-600
                      selection:text-white
                    "
                  >
                    <code>
                      {payload}
                    </code>
                  </pre>

                </motion.div>

              )}

            </AnimatePresence>

          </div>

        </div>

        {/* ==================================================
            FOOTER
        ================================================== */}

        <div
          className="
            shrink-0
            flex items-center justify-between
            gap-3
            px-4 sm:px-5
            py-3.5
            border-t border-white/[0.07]
            bg-slate-900/75
            backdrop-blur-xl
          "
        >

          <button
            type="button"
            onClick={
              onClose
            }
            className="
              min-h-[44px]
              px-4
              rounded-lg
              text-xs
              font-medium
              text-slate-400
              hover:text-slate-100
              hover:bg-white/[0.045]
              transition-colors
            "
          >
            Cancel
          </button>


          <button
            type="button"
            onClick={
              () =>
                onConfirm(
                  script
                )
            }
            className="
              min-h-[44px]
              inline-flex items-center justify-center
              gap-2
              px-4 sm:px-5
              rounded-lg
              border border-indigo-400/25
              bg-indigo-500/90
              hover:bg-indigo-500
              text-white
              text-xs
              font-semibold
              shadow-[0_10px_30px_rgba(79,70,229,0.22)]
              active:scale-[0.98]
              transition-all
            "
          >

            <Play className="w-3.5 h-3.5 fill-current" />

            <span>
              Approve & Execute
            </span>

          </button>

        </div>

      </motion.div>

        </motion.div>
      )}
    </AnimatePresence>
  );
};
